/**
 * PATH: packages/email-hub/src/feature/lib/findInThread.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-FINDINTHREAD-001
 * PURPOSE: Pure match-count logic for the reader's find-in-thread (G5).
 *          Given the thread's messages and a find query, report per-message
 *          match counts split into NEW vs QUOTED content, so the thread can
 *          auto-expand collapsed messages that contain hits and force the
 *          "Show quoted text" section open when the hit lives in the quoted
 *          tail (both are otherwise NOT in the DOM, so the shared
 *          @quotech/ui highlighter can never reach them).
 * WHY:     Gap G5 (email-hub-search-gap-analysis): browser Ctrl+F and the
 *          existing in-page find both miss collapsed messages / hidden
 *          quoted history because unrendered content has no text nodes.
 *          Counting is done here on the RAW body strings (DOMParser ?
 *          textContent), independent of what is currently rendered.
 * FLOW:
 *   (messages, query) ? per message: splitNewVsQuoted(body_html) ?
 *     htmlToText each half ? countOccurrences ? { total, inNew, inQuoted }.
 * DEPENDENCIES:
 *   - ./quoteTrim (splitNewVsQuoted): same new/quoted boundary the
 *     ThreadMessage renderer uses, so counts match what expansion reveals.
 * RELATED:
 *   - hooks/useFindInThread.ts (React memo wrapper)
 *   - components/EmailThread.tsx / ThreadMessage.tsx (consumers)
 *   - @quotech/ui find/findHighlightDom.ts (the DOM highlighter this feeds)
 */

import { splitNewVsQuoted } from './quoteTrim'

/** Minimal message shape needed for counting (subset of hooks Email). */
export interface FindableMessage {
  id: string
  body_html?: string | null
  body_text?: string | null
  body_preview?: string | null
}

/** Per-message match info. */
export interface MessageFindMatches {
  /** Total case-insensitive substring hits across the whole message body. */
  total: number
  /** Hits in the message's own (new) content. */
  inNew: number
  /** Hits inside the quoted-history tail ("Show quoted text" section). */
  inQuoted: number
}

export type ThreadFindState = Record<string, MessageFindMatches>

/**
 * Count non-overlapping case-insensitive occurrences of `needle` in
 * `haystack`. Empty/whitespace needle ? 0 (a blank query matches nothing).
 */
export function countOccurrences(haystack: string, needle: string): number {
  const n = needle.trim().toLowerCase()
  if (!n || !haystack) return 0
  const h = haystack.toLowerCase()
  let count = 0
  let i = h.indexOf(n)
  while (i >= 0) {
    count += 1
    i = h.indexOf(n, i + n.length)
  }
  return count
}

/**
 * Extract readable text from an HTML fragment (script/style dropped by the
 * parser's textContent semantics are acceptable here ? this is a counter,
 * not a renderer). Non-DOM environments fall back to a naive tag strip so
 * the function stays total.
 */
export function htmlToText(html: string | null | undefined): string {
  const raw = html ?? ''
  if (!raw.trim()) return ''
  if (typeof DOMParser === 'undefined') {
    return raw.replace(/<[^>]*>/g, ' ')
  }
  try {
    const doc = new DOMParser().parseFromString(raw, 'text/html')
    return doc.body?.textContent ?? ''
  } catch {
    return raw.replace(/<[^>]*>/g, ' ')
  }
}

/**
 * Compute the find state for a whole thread.
 *
 * Contract:
 *   - Blank query ? {} (nothing matches, nothing force-expands).
 *   - A message with an HTML body is counted over the SAME new/quoted split
 *     the renderer uses (quoteTrim), so "expand reveals the hit" holds.
 *   - A message with no HTML falls back to body_text, then body_preview
 *     (all counted as "new" content ? there is no quoted section to open).
 */
export function computeThreadFindState(
  messages: readonly FindableMessage[],
  query: string,
): ThreadFindState {
  const state: ThreadFindState = {}
  const q = query.trim()
  if (!q) return state

  for (const m of messages) {
    let inNew = 0
    let inQuoted = 0
    if (m.body_html && m.body_html.trim()) {
      const { newHtml, quotedHtml } = splitNewVsQuoted(m.body_html)
      inNew = countOccurrences(htmlToText(newHtml), q)
      inQuoted = countOccurrences(htmlToText(quotedHtml), q)
    } else {
      inNew = countOccurrences(m.body_text || m.body_preview || '', q)
    }
    const total = inNew + inQuoted
    if (total > 0) state[m.id] = { total, inNew, inQuoted }
  }
  return state
}
